import { MultiSelectChipOption } from "src/app/components/multi-select-chips/multi-select-chips.component";
import { WorkPeriod } from "src/app/models/mir-rule.model";
import { WizardId, WizardService } from "../../../services/wizard/wizard.service";
import { makeKey, TripFrequencyUi, TripSemantics } from "../../create-rule/define-duty/define-duty.component";
import { StepProps } from "../../create-rule/step-props";
import { arrayFilterMap } from "../util/array-filter-map";

type TimeHM = { hr: number; min: number };

const canonicalFrequency: Record<TripFrequencyUi, string> = {
	[TripFrequencyUi.trip]: "trip",
	[TripFrequencyUi.workPeriod]: "work_period",
	[TripFrequencyUi.tripWithinWorkperiod]: "trip",
};

function parseTimeToHM(raw: unknown): TimeHM {
	if (typeof raw !== "string" || !raw.trim()) return { hr: 0, min: 0 };
	const s = raw.trim().toUpperCase();
	const ampm = s.endsWith(" AM") || s.endsWith(" PM") ? s.slice(-2) : "";
	const core = ampm ? s.slice(0, -3).trim() : s;
	const [hStr, mStr] = core.split(":");
	let hr = Math.max(0, Math.min(23, Number(hStr) || 0));
	const min = Math.max(0, Math.min(59, Number(mStr) || 0));
	if (ampm) {
		if (ampm === "AM" && hr === 12) hr = 0;
		if (ampm === "PM" && hr !== 12) hr = (hr + 12) % 24;
	}
	return { hr, min };
}

function toApiDefinition(sem: TripSemantics, startCondition: string | null | undefined) {
	if (startCondition === "power") {
		return sem === TripSemantics.tripStart ? "power_on" : "power_off";
	} else if (startCondition === "selected-zone") {
		return sem === TripSemantics.tripStart ? "exit_zone" : "enter_zone";
	}
	return "after_distance"; // fallback
}

function selectedValues(options: MultiSelectChipOption[] | undefined | null): string[] {
	return (options || []).filter(o => o?.selected).map(o => o.value);
}

function toApiWorkPeriod(wp: any) {
	const dayOrder: Array<{ key: string; idx: number }> = [
		{ key: "Su", idx: 0 },
		{ key: "M", idx: 1 },
		{ key: "T", idx: 2 },
		{ key: "W", idx: 3 },
		{ key: "Th", idx: 4 },
		{ key: "F", idx: 5 },
		{ key: "S", idx: 6 },
	];
	const daysObj = (wp?.days ?? {}) as Record<string, boolean>;
	const days = dayOrder.filter(d => !!daysObj[d.key]).map(d => d.idx);

	return {
		days,
		start: parseTimeToHM(wp?.start ?? "00:00"),
		end: parseTimeToHM(wp?.end ?? "23:59"),
	};
}

export function getPayloadCommon(wizardService: WizardService, tripSemantics: TripSemantics) {
	const startCondition =
		(wizardService.getValue(
			WizardId.createRule,
			makeKey(StepProps.DefineDuty_StartCondition, tripSemantics),
		) as string) || "";

	if (!startCondition) {
		return null;
	}
	let timezone =
		(wizardService.getValue(
			WizardId.createRule,
			makeKey(StepProps.DefineDuty_Timezone, tripSemantics),
		) as string) || "";

	const frequency =
		(wizardService.getValue(
			WizardId.createRule,
			makeKey(StepProps.DefineDuty_Frequency, tripSemantics),
		) as string) || "";

	const inspectionTypes =
		(wizardService.getValue(
			WizardId.createRule,
			makeKey(StepProps.DefineDuty_InspectionTypes, tripSemantics),
		) as MultiSelectChipOption[]) || [];

	let rawWorkPeriods = (
		(wizardService.getValue(
			WizardId.createRule,
			makeKey(StepProps.DefineDuty_WorkPeriods, tripSemantics),
		) as WorkPeriod[]) || []
	).filter(arrayFilterMap[StepProps.DefineDuty_WorkPeriods]);

	// clear work periods if user did not select a work period related frequency
	if (frequency !== TripFrequencyUi.workPeriod && frequency !== TripFrequencyUi.tripWithinWorkperiod) {
		rawWorkPeriods = [];
		timezone = null;
	}

	const zones =
		(wizardService.getValue(
			WizardId.createRule,
			makeKey(StepProps.DefineDuty_Zones, tripSemantics),
		) as MultiSelectChipOption[]) || [];

	const expireMinutes = (
		(wizardService.getValue(
			WizardId.createRule,
			makeKey(StepProps.DefineDuty_ExpireMinutes, tripSemantics),
		) as string) + "" || "15"
	).trim();

	const within = Math.max(1, Number.parseInt(expireMinutes, 10) || 15);

	const payload: any = {
		[tripSemantics]: {
			definition: toApiDefinition(tripSemantics, startCondition),
			frequency: canonicalFrequency[frequency],
			// value = [inspectionTypeId, langId]
			inspectionTypes: selectedValues(inspectionTypes).map(v => ({
				inspectionType: v[0],
			})),
			within,
			workPeriods: rawWorkPeriods.length ? rawWorkPeriods.map(toApiWorkPeriod) : null,
			zones:
				startCondition === "selected-zone"
					? selectedValues(zones).length
						? selectedValues(zones)
						: null
					: null,
		},
	};

	if (timezone) {
		payload[tripSemantics].timezone = timezone;
	}

	return payload;
}
